import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  downloadFlightNonOpenersCsv,
  getFlightNonOpenerSummary,
  type FlightNonOpenerSummary,
} from "../../services/campaigns.service";

const EXPORT_ROLES = new Set(["admin", "superadmin", "manager"]);

interface Props {
  flightId: string;
}

export default function FlightNonOpenerExportPanel({ flightId }: Props) {
  const { user } = useAuth();
  const canExport = EXPORT_ROLES.has(
    (user?.role ?? "").toString().toLowerCase(),
  );
  const [summary, setSummary] = useState<FlightNonOpenerSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadSuccess, setDownloadSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!canExport) {
      return;
    }
    let cancelled = false;
    setLoadingSummary(true);
    setSummaryError(null);
    getFlightNonOpenerSummary(flightId)
      .then((value) => {
        if (!cancelled) {
          setSummary(value);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setSummaryError(
            error instanceof Error
              ? error.message
              : "Failed to load non-opener count",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingSummary(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [canExport, flightId]);

  if (!canExport) {
    return null;
  }

  const people = summary?.people ?? 0;
  const hasRows = people > 0;
  const summaryText = hasRows
    ? `${people.toLocaleString()} ${
        people === 1 ? "recipient has" : "recipients have"
      } no recorded open.`
    : "Every send-evidenced recipient has a recorded open, or this flight has not sent yet.";
  let summaryStatus;
  if (loadingSummary) {
    summaryStatus = (
      <p className="mt-2 text-sm text-gray-500">Checking for non-openers…</p>
    );
  } else if (summaryError) {
    summaryStatus = <p className="mt-2 text-sm text-red-600">{summaryError}</p>;
  } else {
    summaryStatus = <p className="mt-2 text-sm text-gray-700">{summaryText}</p>;
  }

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    setDownloadSuccess(null);
    try {
      const result = await downloadFlightNonOpenersCsv(flightId);
      setDownloadSuccess(
        `Downloaded ${result.rowCount.toLocaleString()} ${
          result.rowCount === 1 ? "non-opener" : "non-openers"
        }.`,
      );
    } catch (error: unknown) {
      setDownloadError(
        error instanceof Error ? error.message : "Download failed",
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="px-6 pb-6 pt-2 border-t border-gray-100">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">
              Email non-openers
            </div>
            <p className="mt-1 text-sm text-gray-600">
              Download the send-evidenced recipients in this flight who have no
              recorded open — including name, phone, member status, member ID
              and email address.
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Human, Apple Mail Privacy Protection and security-scanner opens
              all count as opens. A missing open is not proof that someone did
              not read the email because images or tracking may be blocked.
            </p>
            {summaryStatus}
          </div>
          <div className="shrink-0">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!hasRows || downloading || loadingSummary}
              title={
                !hasRows
                  ? "No non-openers are available for this flight"
                  : "Download the non-openers CSV"
              }
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
            >
              {downloading ? "Preparing…" : "Download non-openers CSV"}
            </button>
          </div>
        </div>
        {downloadError && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {downloadError}{" "}
            <button
              type="button"
              onClick={handleDownload}
              className="font-medium underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        )}
        {downloadSuccess && (
          <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
            {downloadSuccess}
          </div>
        )}
      </div>
    </div>
  );
}
